import { Box } from "@mui/material";
import {
  alpha,
  darken,
  lighten,
  styled,
  type Theme,
} from "@mui/material/styles";

import { buildGridTemplateColumns } from "~/components/UI/DataGridHeader/buildGridTemplateColumns";
import {
  GROUPED_ROW_HEIGHT,
  MARKET_ROW_HEIGHT,
} from "~/feature/event/components/MarketsDataGrid/constants";
import { marketColumnSizing } from "~/feature/event/components/MarketsDataGrid/marketColumnWidth";
import {
  getGroupedRowBackground,
  getNotGroupedRowBorder,
} from "~/feature/event/components/MarketsDataGrid/MarketsDataGrid.styles";
import { MarketsDataGridField } from "~/feature/event/components/MarketsDataGrid/types";

// Same tracks the header builds from the same sizing map, so the rows line up
// under it.
const templateColumns = buildGridTemplateColumns(
  Object.values(MarketsDataGridField).map((field) => marketColumnSizing[field])
);

// Mirrors the DataGrid's default row border colour.
const getRowBorderColor = (theme: Theme): string =>
  theme.palette.mode === "light"
    ? lighten(alpha(theme.palette.divider, 1), 0.88)
    : darken(alpha(theme.palette.divider, 1), 0.68);

export const Body = styled(Box)(({ theme }) => ({
  width: "100%",
  overflowX: "auto",
  ...theme.typography.body2,
}));

export const Row = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isGroup" && prop !== "isNote" && prop !== "isStandalone",
})<{ isGroup?: boolean; isNote?: boolean; isStandalone?: boolean }>(
  ({ theme, isGroup = false, isNote = false, isStandalone = false }) => ({
    display: "grid",
    gridTemplateColumns: templateColumns,
    height: isNote ? "auto" : isGroup ? GROUPED_ROW_HEIGHT : MARKET_ROW_HEIGHT,
    borderBottom: isStandalone
      ? getNotGroupedRowBorder(theme)
      : `1px solid ${getRowBorderColor(theme)}`,
    backgroundColor: isGroup
      ? getGroupedRowBackground(theme)
      : theme.palette.background.paper,
  })
);

export const Cell = styled(Box)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  textAlign: "center",

  // The grid pins this column left; `background: inherit` keeps the row's
  // colour under it while the rest scrolls beneath.
  [`&[data-field="${MarketsDataGridField.Market}"]`]: {
    textAlign: "left",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: "inherit",
  },

  [`&[data-row-type="market"]`]: {
    lineHeight: `${MARKET_ROW_HEIGHT - 1}px`,
  },

  [`&[data-row-type="group"]`]: {
    lineHeight: `${GROUPED_ROW_HEIGHT - 1}px`,
  },

  [`&[data-row-type="note"]`]: {
    gridColumn: "1 / -1",
    whiteSpace: "normal",
  },

  "& .MuiTypography-root": {
    lineHeight: theme.typography.caption.lineHeight,
  },
}));

export const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  minHeight: MARKET_ROW_HEIGHT,
  padding: theme.spacing(0, 1),
  borderTop: `1px solid ${getRowBorderColor(theme)}`,
}));
